import { useState } from 'react'
import { archiveGoal, moveToGoal, saveGoal, type GoalProgress } from '../db/budget'
import { formatAmount, formatAriary, parseAmount, parseAmountBounded } from '../lib/money'
import {
  MAX_NOM, MIN_DATE, MOTIF_MONTANT, maxDate, messageDate, messageMontant, messageTexte,
} from '../lib/limites'
import { formatShortDate } from '../lib/dates'
import { Icon } from './Icon'
import './GoalsSection.css'

/**
 * Les objectifs d'épargne — feature 9, décision 2026-08-24 (soir).
 *
 * ⚠️ Distincts de l'enveloppe cumulative « Épargne ». Deux mécanismes de mise
 * de côté coexistent, et ils ne partagent AUCUN argent : l'écran doit le dire,
 * sinon l'utilisateur croira réserver deux fois les mêmes ariary.
 *
 * Ce qui est mis de côté sort de « à placer », jamais de « en poche ». Aucun
 * ariary ne bouge — c'est une intention, pas une dépense.
 */
export function GoalsSection(
  { walletId, goals, available }: {
    walletId: string
    goals: GoalProgress[]
    /** Ce qui reste à placer : on ne propose jamais de mettre de côté au-delà. */
    available: number
  },
) {
  const [creation, setCreation] = useState(false)
  const [versement, setVersement] = useState<string | null>(null)

  return (
    <section className="goals">
      <header className="section-head">
        <h2>Mes objectifs</h2>
        {!creation && (
          <button type="button" onClick={() => setCreation(true)}>Nouvel objectif</button>
        )}
      </header>

      {goals.length === 0 && !creation && (
        <p className="goals-empty">
          Mettre de côté pour quelque chose de précis — une moto, une rentrée
          scolaire, un adidy à venir. L'argent reste dans ta poche, il cesse
          simplement d'être à répartir.
        </p>
      )}

      {creation && (
        <GoalForm onCancel={() => setCreation(false)}
                  onSave={async (valeurs) => {
                    await saveGoal({ walletId, ...valeurs })
                    setCreation(false)
                  }} />
      )}

      <ul className="goals-list">
        {goals.map((progress) => (
          <GoalCard key={progress.goal.id} walletId={walletId} progress={progress}
                    available={available}
                    ouvert={versement === progress.goal.id}
                    onToggle={() => setVersement(
                      versement === progress.goal.id ? null : progress.goal.id)} />
        ))}
      </ul>
    </section>
  )
}

function GoalCard(
  { walletId, progress, available, ouvert, onToggle }: {
    walletId: string
    progress: GoalProgress
    available: number
    ouvert: boolean
    onToggle: () => void
  },
) {
  const { goal, saved, missing, ratio, reached, perCycle } = progress
  const [edition, setEdition] = useState(false)

  return (
    <li className={`goal ${reached ? 'is-reached' : ''}`}>
      <div className="goal-head">
        <span className="goal-name">
          {goal.name}
          {reached && (
            <span className="goal-badge">
              <Icon name="check" size={12} /> Atteint
            </span>
          )}
        </span>
        <span className="goal-amounts tabular">
          <strong>{formatAriary(saved)}</strong>
          {goal.target_amount > 0 && <> sur {formatAriary(goal.target_amount)}</>}
        </span>
      </div>

      {goal.target_amount > 0 && (
        <div className="goal-gauge" aria-hidden="true">
          <span style={{ inlineSize: `${ratio * 100}%` }} />
        </div>
      )}

      <p className="goal-state">
        {reached
          ? 'Objectif atteint. Bravo.'
          : goal.target_amount > 0
            ? <>Il manque <strong className="tabular">{formatAriary(missing)}</strong></>
            : 'Sans montant visé — tu mets ce que tu veux.'}
        {goal.target_date && !reached && (
          <> · avant le {formatShortDate(goal.target_date)}</>
        )}
        {/* Un rythme PAR CYCLE et non par jour : l'utilisateur met de côté au
            rythme où l'argent lui arrive, et « 1 240 Ar par jour » ne se
            rapporte à aucun geste qu'il puisse faire. */}
        {perCycle !== null && (
          <> — soit <strong className="tabular">{formatAriary(perCycle)}</strong> par cycle</>
        )}
      </p>

      {edition ? (
        <GoalForm goal={goal} onCancel={() => setEdition(false)}
                  onSave={async (valeurs) => {
                    await saveGoal({ walletId, id: goal.id, ...valeurs })
                    setEdition(false)
                  }} />
      ) : ouvert ? (
        <MoveForm available={available} saved={saved}
                  onCancel={onToggle}
                  onMove={async (amount, direction) => {
                    await moveToGoal({ walletId, goalId: goal.id, amount, direction })
                    onToggle()
                  }} />
      ) : (
        <div className="goal-actions">
          <button type="button" className="is-primary" onClick={onToggle}>
            Mettre de côté
          </button>
          <button type="button" onClick={() => setEdition(true)}>Modifier</button>
          {/* Archiver et non supprimer : les versements décrivent des mois de
              mise de côté, les effacer ferait disparaître ce que l'utilisateur
              a réellement accompli. */}
          <button type="button" onClick={() => void archiveGoal(goal.id)}>Ranger</button>
        </div>
      )}
    </li>
  )
}

function GoalForm(
  { goal, onCancel, onSave }: {
    goal?: { name: string; target_amount: number; target_date: string | null }
    onCancel: () => void
    onSave: (v: { name: string; targetAmount: number; targetDate: string | null }) => void
  },
) {
  const [name, setName] = useState(goal?.name ?? '')
  const [amount, setAmount] = useState(
    goal?.target_amount ? formatAmount(goal.target_amount) : '')
  const [date, setDate] = useState(goal?.target_date ?? '')

  /*
   * Le premier problème rencontré, et lui seul : empiler trois messages sous
   * un formulaire de trois champs se lit comme un échec général alors qu'une
   * seule ligne cloche.
   *
   * Le champ VIDE n'est pas un problème ici — c'est `required` qui s'en charge,
   * au moment de l'envoi. Le dire dès l'ouverture reprocherait à l'utilisateur
   * de ne pas avoir encore commencé.
   */
  const probleme = messageTexte(name, MAX_NOM, { quoi: 'Le nom' })
    ?? messageMontant(amount)
    ?? messageDate(date)

  return (
    <form className="goal-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (probleme) return
            onSave({
              name: name.trim(),
              targetAmount: parseAmountBounded(amount) ?? 0,
              targetDate: date || null,
            })
          }}>
      <label>
        <span>Pour quoi ?</span>
        <input type="text" required maxLength={MAX_NOM} placeholder="une moto, la rentrée…"
               value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label>
        <span>Montant visé <em>(facultatif)</em></span>
        <input type="text" inputMode="numeric" className="tabular" placeholder="0"
               pattern={MOTIF_MONTANT} title="Un montant s’écrit en chiffres."
               value={amount} onChange={(e) => setAmount(e.target.value)} />
      </label>
      <label>
        <span>Pour quand ? <em>(facultatif)</em></span>
        {/* Bornes portées par le champ LUI-MÊME en plus du message : le
            sélecteur de date du navigateur refuse alors l'an 9999 d'emblée. */}
        <input type="date" value={date} min={MIN_DATE} max={maxDate()}
               onChange={(e) => setDate(e.target.value)} />
      </label>
      {probleme && <p className="goal-erreur" role="alert">{probleme}</p>}
      <div className="goal-form-actions">
        <button type="submit" className="is-primary" disabled={probleme !== null}>
          Enregistrer
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}

function MoveForm(
  { available, saved, onCancel, onMove }: {
    available: number
    saved: number
    onCancel: () => void
    onMove: (amount: number, direction: 'deposit' | 'withdrawal') => void
  },
) {
  const [montant, setMontant] = useState('')
  const [sens, setSens] = useState<'deposit' | 'withdrawal'>('deposit')
  const valeur = parseAmount(montant) ?? 0
  // On ne laisse pas mettre de côté plus qu'il n'y a à placer, ni reprendre
  // plus qu'on n'a mis : les deux produiraient un « à placer » faux.
  const plafond = sens === 'deposit' ? Math.max(0, available) : saved
  const trop = valeur > plafond

  return (
    <form className="goal-move"
          onSubmit={(e) => { e.preventDefault(); if (!trop && valeur > 0) onMove(valeur, sens) }}>
      <div className="goal-sens">
        <button type="button" aria-pressed={sens === 'deposit'}
                onClick={() => setSens('deposit')}>Mettre de côté</button>
        <button type="button" aria-pressed={sens === 'withdrawal'}
                onClick={() => setSens('withdrawal')}>Reprendre</button>
      </div>
      <label>
        <span>Combien ?</span>
        <input type="text" inputMode="numeric" className="tabular" autoFocus placeholder="0"
               pattern={MOTIF_MONTANT} title="Un montant s’écrit en chiffres."
               value={montant} onChange={(e) => setMontant(e.target.value)} />
      </label>
      <p className={`goal-limit ${trop ? 'is-over' : ''}`}>
        {sens === 'deposit'
          ? <>Tu peux mettre jusqu'à <strong className="tabular">{formatAriary(plafond)}</strong></>
          : <>Tu as mis <strong className="tabular">{formatAriary(plafond)}</strong> de côté</>}
      </p>
      <div className="goal-form-actions">
        <button type="submit" className="is-primary" disabled={trop || valeur <= 0}>
          {sens === 'deposit' ? 'Mettre de côté' : 'Reprendre'}
        </button>
        <button type="button" onClick={onCancel}>Annuler</button>
      </div>
    </form>
  )
}
